import React, { useState, useEffect } from 'react';

/**
 * Reset Password Form Component
 * Handles password reset with token from email link
 */
export default function ResetPasswordForm() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    // Check if we have a recovery token in the URL hash
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const type = hashParams.get('type');
    
    if (accessToken && type === 'recovery') {
      setHasToken(true);
    } else {
      setError('Invalid or missing reset token. Please request a new password reset link.');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      // Get Supabase client from window (set by auth.js)
      const supabase = window.supabaseClient;
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) throw updateError;

      setSuccess(true);

      // Redirect to login after 3 seconds
      setTimeout(() => {
        window.location.href = '/login/';
      }, 3000);

    } catch (err) {
      console.error('Password reset error:', err);
      setError(err.message || 'Failed to reset password. Please try again.');
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <section className="py-12 md:py-16">
        <div className="container-custom max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Password Reset Successful</h1>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
              <p className="font-medium mb-2">Your password has been reset!</p>
              <p className="text-sm">
                You can now log in with your new password. Redirecting you to the login page...
              </p>
            </div>

            <div className="text-center">
              <a
                href="/login/"
                className="text-brand-700 hover:text-brand-800 font-medium"
              >
                Go to login page
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (!hasToken) {
    return (
      <section className="py-12 md:py-16">
        <div className="container-custom max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Reset Password</h1>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <p className="font-medium mb-2">Invalid Reset Link</p>
              <p className="text-sm">
                This password reset link is invalid or has expired. Please request a new one.
              </p>
            </div>

            <div className="text-center">
              <a
                href="/forgot-password/"
                className="bg-brand-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-brand-700 transition-colors duration-200 inline-block"
              >
                Request New Reset Link
              </a>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16">
      <div className="container-custom max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Reset Password</h1>
          <p className="text-lg text-gray-600">Enter your new password</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                New Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Enter new password (min. 6 characters)"
                disabled={isLoading}
                minLength={6}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Confirm new password"
                disabled={isLoading}
                minLength={6}
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-brand-600 text-white font-semibold py-3 px-6 rounded-lg hover:bg-brand-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Resetting Password...' : 'Reset Password'}
              </button>
            </div>

            <div className="text-center text-sm text-gray-600">
              Remember your password?{' '}
              <a href="/login/" className="text-brand-700 hover:text-brand-800 font-medium">
                Log in
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
